import { Component, ViewChild } from '@angular/core'
import {
  Currency,
  Depot,
  File,
  ProductMove,
  ProductMovePack,
  PurchaseOrder,
  PurchaseOrderItem,
} from 'depoto-core/src/entities'
import { CoreService, NotificationService } from '../../services'
import { Supplier, Tag } from 'depoto-core/src/entities'
import { ListingItem } from 'depoto-core/src/models'
import { productMovePacksPurchaseOrderColumns } from '../../utils/definitions.util'
import { ActivatedRoute, Router } from '@angular/router'
import { FmtDatePipe } from '../../pipes'
import { roundNum, SchemaUtil, getThumbUrl as imageGetThumbUrl } from '../../utils'
import { BaseListing } from '../_base-listing/base-listing'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { ModalQrCodeComponent } from '../../components/modal-components/modal-qrcode/modal-qrcode.component'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-product-purchase-order-creation',
  templateUrl: './product-purchase-order-creation.component.html',
  styleUrls: ['./product-purchase-order-creation.component.scss'],
  providers: [FmtDatePipe],
})
export class ProductPurchaseOrderCreationComponent extends BaseListing implements CheckDataChanges {
  @ViewChild('modalQr') modalQr: ModalQrCodeComponent
  depots: Depot[] = []
  suppliers: Supplier[] = []
  tags: Tag[] = []
  loading = true
  reloading = false
  currentPage: number
  endPage: number
  totalItems = 0
  items: any
  public isCreationModeEnabled = true
  public isEditModeEnabled = true
  public canEdit = false
  public productList: Array<any> = []
  purchaseOrderInfo: Partial<PurchaseOrderInfo> = {}
  public purchaseOrder: Partial<PurchaseOrder> | any | undefined
  readonly purchaseOrderId
  priceSum = 0
  priceWithVatSum = 0
  purchaseCurrencyFromCompany: Currency // there is no way to change this across hs, so it's certain we can use this
  activeTab: 'common' | 'pmps' = 'common'
  hasUnsavedChanges = false
  objectForChangesCheck: string

  productMovePacks: ProductMovePack[] = []
  listingColumns: ListingItem[] = productMovePacksPurchaseOrderColumns
  storageKey: { type: string; key: string }
  sortProp = 'created'
  sortDir = 'desc'

  constructor(
    protected core: CoreService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fmtDatePipe: FmtDatePipe,
    private readonly translationService: TranslateService,
    private readonly notificationService: NotificationService,
  ) {
    super(core)
    this.purchaseOrderId = +this.route.snapshot.params.id
    const selectedProducts = this.router.getCurrentNavigation()?.extras?.state?.selectedProducts
    this.depots = this.core.lists.depots.filter(depot => {
      return depot.name !== 'all' && this.core.services.user?.user?.depots?.some(d => d.id === depot.id)
    })
    this.suppliers = this.core.lists.suppliers
    this.tags = this.core.lists.tags.filter(tag => tag.type === 'purchaseOrder')
    this.purchaseCurrencyFromCompany = this.core.services.user.user?.company?.currency
    if (this.purchaseOrderId) {
      this.fetch(this.purchaseOrderId).then(() => (this.objectForChangesCheck = JSON.stringify(this.productList)))
      this.isCreationModeEnabled = false
      this.isEditModeEnabled = false
      this.getStorageKey('productMovePacksPurchaseOrder')
      if (!this.isCreationModeEnabled) this.filter(1)
      this.canEdit = this.core.services.user.hasRole('ROLE_PURCHASE_ORDER_UPDATE')
    } else if (selectedProducts) {
      this.productList.push(...selectedProducts)
      this.purchaseOrderInfo.depot = this.router.getCurrentNavigation().extras.state.depot
      this.loading = false
    } else {
      this.loading = false
    }
  }

  hasUnsavedDataChanges(): boolean {
    // return this.hasUnsavedChanges
    // return this.isEditModeEnabled
    return this.objectForChangesCheck !== JSON.stringify(this.productList)
  }

  public async fetch(id: number) {
    this.purchaseOrder = await this.core.services.purchase.getById(id, SchemaUtil.purchaseOrderDetail)
    this.setAdditionalInfo(this.purchaseOrder)
    this.purchaseCurrencyFromCompany = this.core.services.user.user.company.currency
    this.productList = this.purchaseOrder.items.map(value => {
      return (({
        id,
        quantity,
        quantityIn,
        purchasePrice,
        purchasePriceAll,
        currency,
        product,
        note,
        status,
        externalReference,
      }) => ({
        id,
        product: product.id,
        quantity,
        quantityIn,
        purchasePrice,
        purchasePriceWithVat: roundNum(purchasePrice / product.vat.coefficient),
        // purchasePriceWithVat: roundNum(purchasePrice * Number(`1.${product.vat.percent}`)),
        purchasePriceAll,
        // purchasePriceAllWithVat: roundNum(purchasePrice * Number(`1.${product.vat.percent}`) * quantity),
        purchasePriceAllWithVat: roundNum((purchasePrice / product.vat.coefficient) * quantity),
        currency: this.purchaseCurrencyFromCompany,
        name: product.name,
        fullName: product.fullName,
        mainImage: product.mainImage,
        code: product.code,
        ean: product.ean,
        note,
        externalReference,
        status,
        vat: product.vat,
        isValid: true,
        tags: product.tags || [],
        packs: product.packs || [],
      }))(value)
    })
    this.calcSums()
  }

  private setAdditionalInfo(purchaseOrder: PurchaseOrder) {
    if (!purchaseOrder) {
      return
    }
    this.purchaseOrderInfo = (({
      supplier,
      depot,
      tags,
      externalId,
      externalReference,
      note,
      privateNote,
      created,
      createdBy,
      dateEstimatedArrival,
    }) => ({
      supplier: supplier?.id,
      depot: depot?.id,
      note: note,
      privateNote: privateNote,
      dateEstimatedArrival: this.fmtDatePipe.transform(dateEstimatedArrival, 'yyyy-MM-dd'),
      tags: tags?.map(tags => tags.id),
      externalId: externalId,
      externalReference,
      createdBy: createdBy?.name || 'AUTO',
      created: this.fmtDatePipe.transform(created, 'yyyy-MM-dd'),
    }))(purchaseOrder)
  }

  getThumbUrl(image: File): string {
    return imageGetThumbUrl(image)
  }

  async fetchProductsUnderMinimum() {
    const depotId = Number(this.purchaseOrderInfo.depot)
    const supplierId = Number(this.purchaseOrderInfo.supplier)
    if (isNaN(depotId) || depotId < 1 || isNaN(supplierId) || supplierId < 1) {
      alert(this.translationService.instant('depot-and-supplier.select'))
      return
    }
    const params = {
      filters: {
        listType: 'children_and_parents_without_children',
        metrics: 'less_than_minimum',
        metricDepots: [depotId],
        defaultSuppliers: [supplierId],
        enabled: true,
      },
    }
    const allProducts = []
    this.loading = true
    this.core.services.product.getList(params, SchemaUtil.productList).then(result => {
      const endPageNumber = result?.paginator?.endPage || 1
      const promList = []
      for (let i = 1; i <= endPageNumber; i++) {
        promList.push(this.core.services.product.getList({ ...params, page: i }, SchemaUtil.product))
      }
      Promise.all(promList)
        .then(response => {
          response.forEach(row => {
            row.items.forEach(product => allProducts.push(product))
          })
        })
        .finally(() => {
          this.loading = false
          if (!allProducts?.length) {
            alert(this.translationService.instant('no-products-under-minimum'))
            return
          }
          this.productList = []
          for (const prod of allProducts) {
            let packQuantity = 1
            if (prod.tags?.find(tag => tag.externalId === 'hs-count-by-piece')) {
              packQuantity = prod.packs?.find(pack => pack.externalId === 'hs-count-by-piece')?.quantity
            }
            const p = {
              code: prod.code,
              ean: prod.ean,
              name: prod.name,
              fullName: prod.fullName,
              mainImage: prod.mainImage,
              note: prod.note,
              product: prod.id,
              currency: this.purchaseCurrencyFromCompany,
              purchasePrice: prod.purchasePrice,
              quantity:
                prod.metrics.find(m => m.depot.id === depotId)?.quantityOptimum -
                prod.quantities.find(q => q.depot.id === depotId)?.quantityAvailable -
                prod.metrics.find(m => m.depot.id === depotId)?.quantityPurchase * packQuantity,
              vat: prod.vat,
              tags: prod.tags,
              packs: prod.packs,
            }
            // console.log('metrics - optimum', prod.metrics.find(m => m.depot.id === depotId)?.quantityOptimum)
            // console.log('metrics - avail', prod.quantities.find(q => q.depot.id === depotId)?.quantityAvailable)
            // console.log('metrics - on the way * packQ', prod.metrics.find(m => m.depot.id === depotId)?.quantityPurchase * packQuantity)
            if (packQuantity > 1) {
              p.quantity = Math.ceil(p.quantity / packQuantity) // always take whole package
            }
            if (p.quantity > 0) {
              this.addProduct(p)
            }
          }
          if (!this.productList.length) {
            alert(this.translationService.instant('no-products-under-minimum'))
            return
          }
        })
    })
  }

  public async addProduct($event: any) {
    const product = (({
      code,
      ean,
      name,
      fullName,
      mainImage,
      note,
      product,
      currency,
      tags,
      packs,
      purchasePrice,
      quantity,
      vat,
    }) => ({
      code,
      ean,
      name,
      fullName,
      mainImage,
      note,
      product,
      currency: this.purchaseCurrencyFromCompany,
      tags,
      packs,
      purchasePrice,
      purchasePriceWithVat: roundNum(purchasePrice / vat.coefficient),
      // purchasePriceWithVat: roundNum(purchasePrice * Number(`1.${vat.percent}`)),
      quantity,
      purchasePriceAll: quantity * purchasePrice,
      purchasePriceAllWithVat: roundNum(quantity * (purchasePrice / vat.coefficient)),
      // purchasePriceAllWithVat: roundNum(quantity * purchasePrice * Number(`1.${vat.percent}`)),
      vat,
      isValid: true,
      status: { id: 'new' },
    }))($event)
    if (this.productList.find(p => p.product === product.product)) {
      this.rowChange(product, 'quantity', `${this.productList.find(p => p.product === product.product).quantity + 1}`)
      return
    }
    this.productList.push(product)
    this.calcSums()
  }

  rowChange(item: any, property: 'purchasePrice' | 'purchasePriceWithVat' | 'quantity', nextValue: string | number) {
    if (typeof nextValue === 'number') {
      nextValue = nextValue.toFixed(2)
    }
    if (nextValue.endsWith('.') || nextValue.endsWith(',')) {
      return
    }
    if (nextValue.includes(',')) {
      nextValue = nextValue.replace(',', '.')
    }
    if (isNaN(Number(nextValue))) {
      item.isValid = false
      return
    }
    if (
      property === 'quantity' &&
      (Number(nextValue) < 1 || Number(nextValue) !== Number(Number(nextValue).toFixed(0)))
    ) {
      item.isValid = false
      return
    }
    const nextItem = { ...item }
    switch (property) {
      case 'purchasePrice':
        nextItem.purchasePrice = Number(nextValue)
        nextItem.purchasePriceWithVat = Number(nextValue) / nextItem.vat.coefficient
        // nextItem.purchasePriceWithVat = Number(nextValue) * Number(`1.${nextItem.vat.percent}`)
        nextItem.purchasePriceAll = Number(nextValue) * nextItem.quantity
        nextItem.purchasePriceAllWithVat = (Number(nextValue) / nextItem.vat.coefficient) * nextItem.quantity
        // nextItem.purchasePriceAllWithVat = Number(nextValue) * Number(`1.${nextItem.vat.percent}`) * nextItem.quantity
        break
      case 'purchasePriceWithVat':
        nextItem.purchasePrice = Number(nextValue) * nextItem.vat.coefficient
        nextItem.purchasePriceWithVat = Number(nextValue)
        nextItem.purchasePriceAll = Number(nextValue) * nextItem.vat.coefficient * nextItem.quantity
        nextItem.purchasePriceAllWithVat = Number(nextValue) * nextItem.quantity
        break
      case 'quantity':
        nextItem.quantity = Number(nextValue)
        nextItem.purchasePriceAll = nextItem.purchasePrice * nextItem.quantity
        nextItem.purchasePriceAllWithVat = nextItem.purchasePriceWithVat * nextItem.quantity
        break
    }
    this.productList.forEach((row, i) => {
      if (row.product === item.product) {
        this.productList[i].purchasePrice = roundNum(nextItem.purchasePrice)
        this.productList[i].purchasePriceWithVat = roundNum(nextItem.purchasePriceWithVat)
        this.productList[i].purchasePriceAll = roundNum(nextItem.purchasePriceAll)
        this.productList[i].purchasePriceAllWithVat = roundNum(nextItem.purchasePriceAllWithVat)
        this.productList[i].quantity = nextItem.quantity
        this.productList[i].isValid = true
      }
    })
    this.calcSums()
  }

  calcSums() {
    this.priceSum = this.productList.map(p => p.purchasePriceAll).reduce((a, b) => a + b, 0)
    this.priceWithVatSum = this.productList.map(p => p.purchasePriceAllWithVat).reduce((a, b) => a + b, 0)
    this.hasUnsavedChanges = true
  }

  stockUpAction(event) {
    switch (event) {
      case 'stockUp':
        this.stockUp()
        break
      case 'stockUpInMobileApp':
        this.stockUpInMobileApp()
        break
    }
  }

  stockUp(item?: any) {
    const operations = []
    if (item) {
      const amount = item.quantity - item.quantityIn
      operations.push(
        new ProductMove({
          amount,
          product: {
            id: item.product,
            name: item.fullName,
            fullName: item.fullName,
            purchaseCurrency: item.currency,
            mainImage: item.mainImage,
            enabledBatchExpiration: true,
            tags: item.tags,
            packs: item.packs,
          },
          purchasePrice: item.purchasePrice,
          purchaseOrderItem: item,
          supplier: this.purchaseOrderInfo.supplier,
        }),
      )
    } else {
      this.productList.forEach(item => {
        if (item.quantity - item.quantityIn > 0) {
          operations.push(
            new ProductMove({
              amount: item.quantity - item.quantityIn,
              product: {
                id: item.product,
                name: item.fullName,
                fullName: item.fullName,
                purchaseCurrency: item.currency,
                mainImage: item.mainImage,
                enabledBatchExpiration: true,
                tags: item.tags,
                packs: item.packs,
              },
              purchasePrice: item.purchasePrice,
              purchaseOrderItem: item,
              supplier: this.purchaseOrderInfo.supplier,
            }),
          )
        }
      })
    }
    const stockIn = {
      depotTo: this.purchaseOrderInfo.depot,
      purchaseOrderId: this.purchaseOrderId,
      operations,
    }
    this.router.navigate(['stock-batch-in'], { state: { stockIn } })
  }

  async stockUpCancel(item) {
    if (!confirm(this.translationService.instant('cancel.confirm.item'))) {
      return
    }
    const res = await this.core.services.purchase.updatePurchaseOrderItem({ id: item.id, status: 'cancelled' })
    await this.fetch(this.purchaseOrderId)
  }

  async stockUpCancelRest(item) {
    if (!confirm(this.translationService.instant('cancel.confirm.items'))) {
      return
    }
    const res = await this.core.services.purchase.updatePurchaseOrderItem({ id: item.id, quantity: item.quantityIn })
    await this.fetch(this.purchaseOrderId)
  }

  stockUpInMobileApp() {
    this.modalQr.showChildModal(`PO-${this.purchaseOrderId}`)
  }

  export(type: string) {
    this.core.services.download.downloadDocumentAndShowInNewWindow(this.purchaseOrder[type])
  }

  public async create(): Promise<void> {
    // if (!this.purchaseOrderInfo.externalReference?.length) {
    //   this.notificationService.error(this.translationService.instant('purchaseOrder.error.externalReference.empty'))
    //   return
    // }
    if (this.isCreationModeEnabled) {
      const result = await this.core.services.purchase.create(this.collectInfo(), { id: null })
      this.hasUnsavedChanges = false
      this.isEditModeEnabled = false
      this.objectForChangesCheck = JSON.stringify(this.productList)
      this.router.navigate(['/purchase-order', result.id], {})
      this.notificationService.success(`Naskladňovací objednávka byla úspěšně vytvořena.`)
    } else {
      await this.core.services.purchase.update(this.collectInfo())
      this.notificationService.success(`Naskladňovací objednávka byla úspěšně aktualizována.`)
      await this.fetch(this.purchaseOrder?.id)
      this.hasUnsavedChanges = false
      this.isEditModeEnabled = false
      this.objectForChangesCheck = JSON.stringify(this.productList)
    }
  }

  private collectInfo() {
    const common = {
      supplier: this.purchaseOrderInfo.supplier,
      depot: this.purchaseOrderInfo.depot,
      note: this.purchaseOrderInfo.note,
      externalReference: this.purchaseOrderInfo.externalReference,
      items: this.productList.map(value => {
        return {
          product: value.product,
          quantity: value.quantity,
          purchasePrice: isNaN(Number(value.purchasePrice)) ? 0 : Number(value.purchasePrice),
          note: value.note || '',
          currency: this.purchaseCurrencyFromCompany.id,
          externalId: value.externalId || '',
        }
      }),
    }
    return this.isCreationModeEnabled ? common : { ...common, id: this.purchaseOrderId }
  }

  public async remove(item: PurchaseOrderItem) {
    if (item.id) {
      await this.core.services.purchase
        .deletePurchaseOrderItem(item.id)
        .then(value => {
          this.removeProductFromList(item)
          this.notificationService.success(`Produkt byl úspěšně smazán z naskladňovací objednávky.`)
        })
        .catch(err => this.notificationService.error(`Produkt se nepodařilo smazat z naskladňovací objednávky.`))
    } else {
      this.removeProductFromList(item)
    }
  }

  private removeProductFromList(item: PurchaseOrderItem): void {
    this.productList = this.productList.filter(value => value.product !== item.product)
    this.calcSums()
  }

  public async delete() {
    if (
      confirm(this.translationService.instant('purchaseOrder.confirm.deletion', { number: this.purchaseOrder.number }))
    ) {
      await this.core.services.purchase
        .deletePurchaseOrder(this.purchaseOrderId)
        .then(value => {
          this.notificationService.success(`Naskladňovací objednávka byla úspěšně smazána.`)
        })
        .catch(err => this.notificationService.error(`Naskladňovací objednávku se nepodařilo smazat.`))
      this.hasUnsavedChanges = false
      this.router.navigate(['/purchase-orders'])
    } else {
      return
    }
  }

  filter(pageNo = 1, sortProp: string = this.sortProp, sortDir: string = this.sortDir, concat = false): void {
    this.currentPage = pageNo

    this.loading = true
    if (!concat) this.reloading = true

    this.core.services.productMovePack
      .getList(
        {
          page: pageNo,
          sort: sortProp,
          direction: sortDir,
          filters: {
            type: 'in',
            purchaseOrder: this.purchaseOrderId,
          },
        },
        SchemaUtil.productMovePackList,
      )
      .then(result => {
        this.loading = false
        this.reloading = false
        if (!concat) {
          this.productMovePacks = result.items
        } else {
          result.items.forEach(movePack => {
            this.productMovePacks.push(new ProductMovePack(movePack))
          })
        }
        this.totalItems = result.paginator ? result.paginator.totalCount : 0
        this.endPage = result.paginator ? result.paginator.endPage : 0
      })
  }

  callAction(event: any): void {
    this[event.action](event.item)
  }

  navigateBack() {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      this.router.navigate(['/purchase-order-fork'])
    }
  }
}

type PurchaseOrderInfo = {
  supplier: number
  depot: number
  note: string
  privateNote: string
  dateEstimatedArrival: string
  tags: Array<number>
  reference: string
  externalId: string
  externalReference: string
  createdBy: string
  created: string
}
